import { z } from "zod";
import {
    MAX_SCHEDULE_MILESTONE_OFFSET,
    MAX_SCHEDULE_MILESTONES,
    MAX_SCHEDULE_MILESTONE_NAME_LENGTH,
} from "@common/constants/domains/accounting/schedules";
import {
    createScheduleMilestoneTimingRefine,
    refineScheduleMilestones,
} from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import {
    ScheduleMilestoneAnchor,
    ScheduleMilestoneOffsetUnit,
} from "@common/types/domains/accounting/schedule";
import {
    allocationPercentFormSchema,
    scheduleComponentRowFormSchema,
} from "./scheduleComponentRow.schema";

export const MILESTONE_DESCRIPTION_REQUIRED_MESSAGE = "Enter a description";
export const MILESTONE_COUNT_REQUIRED_MESSAGE = "Enter a count";
export const MILESTONE_DATE_REQUIRED_MESSAGE = "Select a date";

const milestoneTimingRefine = createScheduleMilestoneTimingRefine({
    offsetCountRequired: MILESTONE_COUNT_REQUIRED_MESSAGE,
    specificDateRequired: MILESTONE_DATE_REQUIRED_MESSAGE,
});

const milestoneRowFieldsSchema = z.object({
    key: z.string(),
    name: z
        .string()
        .trim()
        .min(1, MILESTONE_DESCRIPTION_REQUIRED_MESSAGE)
        .max(
            MAX_SCHEDULE_MILESTONE_NAME_LENGTH,
            `Use ${MAX_SCHEDULE_MILESTONE_NAME_LENGTH} characters or fewer`,
        ),
    allocationPercent: allocationPercentFormSchema,
    anchor: z.nativeEnum(ScheduleMilestoneAnchor),
    offsetCount: z
        .number()
        .int("Enter a whole number")
        .positive(MILESTONE_COUNT_REQUIRED_MESSAGE)
        .max(
            MAX_SCHEDULE_MILESTONE_OFFSET,
            `Use ${MAX_SCHEDULE_MILESTONE_OFFSET} or fewer`,
        )
        .nullable(),
    offsetUnit: z.nativeEnum(ScheduleMilestoneOffsetUnit).nullable(),
    specificDate: z.string().date(MILESTONE_DATE_REQUIRED_MESSAGE).nullable(),
});

export const milestoneRowFormSchema = milestoneRowFieldsSchema.superRefine(
    milestoneTimingRefine,
);

/** Only the fields that place a milestone on the calendar. */
export const milestoneTimingFormSchema = milestoneRowFieldsSchema
    .pick({
        anchor: true,
        offsetCount: true,
        offsetUnit: true,
        specificDate: true,
    })
    .superRefine(milestoneTimingRefine);

export const milestoneRowsFormSchema = z
    .array(milestoneRowFormSchema)
    .max(MAX_SCHEDULE_MILESTONES);

export const milestonePlanSchema = z
    .object({
        components: z.array(
            scheduleComponentRowFormSchema.pick({
                method: true,
                allocationPercent: true,
            }),
        ),
        milestones: milestoneRowsFormSchema,
    })
    .superRefine(refineScheduleMilestones);
